import { client } from "../client";
import type { Paginated, PaginatedFilter } from "../types/axios.types";
import type { Command, CommandTracking } from "../types/command.types";
import type { ActivityLog } from "../types/logs.types";
import type { Ticket } from "../types/ticket.types";

export const fetchCommandActivityLogsPaginated = async (
	id: string | number | undefined,
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
) => {
	if (id === undefined) {
		return null;
	}
	const params = new URLSearchParams();
	// Pagination
	params.append("page", page ? page.toString() : "1");
	params.append("size", size ? size.toString() : "50");
	// Search
	if (search) {
		params.append("search", search);
	}
	// Sort
	if (sortBy) {
		sortBy.forEach((key) => params.append("sort", key));
	}
	// Filters
	if (filters) {
		Object.keys(filters).forEach((key: string) => {
			// Blacklist some keys
			if (
				["page", "size", "search", "sort"].includes(key) ||
				filters[key] === ""
			) {
				return;
			}
			params.append(
				key,
				Array.isArray(filters[key]) ? filters[key].join(",") : filters[key],
			);
		});
	}
	// Query
	const response = await client.get<Paginated<ActivityLog[]>>(
		`commands/${id}/logs`,
		{
			params,
		},
	);
	return response.data;
};

export const fetchCommandsPaginated = async (
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
) => {
	const params = new URLSearchParams();
	// Pagination
	params.append("page", page ? page.toString() : "1");
	params.append("size", size ? size.toString() : "50");
	// Search
	if (search) {
		params.append("search", search);
	}
	// Sort
	if (sortBy) {
		sortBy.forEach((key) => params.append("sort", key));
	}
	// Filters
	if (filters) {
		Object.keys(filters).forEach((key: string) => {
			// Blacklist some keys
			if (
				["page", "size", "search", "sort"].includes(key) ||
				filters[key] === ""
			) {
				return;
			}
			params.append(
				key,
				Array.isArray(filters[key]) ? filters[key].join(",") : filters[key],
			);
		});
	}
	// Query
	const response = await client.get<Paginated<Command[]>>(`commands/`, {
		params,
	});
	return response.data;
};

export const fetchCommand = async (
	id: number | string | null,
): Promise<Command | null> => {
	if (id === undefined) {
		return null;
	}
	const response = await client.get<Command | null>(`commands/${id}/`);
	if (response.status === 204) {
		return null;
	}
	return response.data;
};

export const fetchCommandSupplyOrder = async (
	id: number | string | null,
): Promise<object | null> => {
	if (id === undefined) {
		return null;
	}
	const response = await client.get(`commands/${id}/supply/order`);
	if (response.status === 204) {
		return null;
	}
	return response.data;
};

export const fetchCommandByExposedId = async (
	exposedId: string | null,
): Promise<Command | null> => {
	if (exposedId === undefined) {
		return null;
	}
	const response = await client.get<Command | null>(
		`commands/eid/${exposedId}/`,
	);
	if (response.status === 204) {
		return null;
	}
	return response.data;
};

export const fetchCommandTickets = async (
	id: number | string | undefined,
): Promise<Ticket[]> => {
	if (id === undefined) {
		return [];
	}
	const response = await client.get<Ticket[]>(`commands/tickets/${id}/`);
	if (response.status === 204) {
		return [];
	}
	return response.data;
};

export const fetchCommandUPSTracking = async (
	id?: number | string | null,
): Promise<CommandTracking[]> => {
	if (id === undefined) {
		return [];
	}
	const response = await client.get<CommandTracking[]>(
		`commands/tracking/${id}/`,
	);
	return response.data;
};
